/**
 * Every read the Console makes, as a query (replaces the hand-rolled `Resource`).
 *
 * `Resource` was a class per screen holding `loading | ready | error`, a manual
 * `reload()`, and a repaint callback. What it never had was a cache, so opening
 * the board twice fetched it twice, and every mutation had to remember which
 * resources it invalidated by hand — which is how the roster went stale after a
 * pause and nobody noticed until it was pointed out.
 *
 * Keys are arrays so a mutation can invalidate a family: `['board']` covers the
 * card list and every open card. Human Review follows that same workflow-state
 * invalidation path; there is no separate Decisions inbox or polling badge.
 */

import { useEffect } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { ApiError, isTaskNotFoundError } from '../client/api.js';
import { mutationContext, type ConsoleDataSource } from '../client/data-source.js';
import {
  releaseDetailQueryKey,
  releaseEventInvalidates,
  releaseListQueryKey,
} from '../logic/releases.js';
import type {
  HomeThreadMessage,
  TeamListItem,
  ApprovalReceiptView,
  AuditEntry,
  BoardView,
  GateView,
  WorkflowView,
  WorkflowLayoutView,
  PackageOption,
  IntegrationView,
  MainOperatorThread,
  OperatorChatSummary,
  OperatorChatThread,
  OperatorStatusView,
  HumanReviewRequestView,
  RoleSummary,
  ReleaseOperationView,
  ReleasePageView,
  RunView,
  SkillPackageView,
  TaskListItem,
  TaskChecklistView,
  TeamStatus,
  TeamTemplate,
  TeamVersionSummary,
  CredentialSummaryView,
  ManagedLoginView,
  ProviderConnectionView,
  StageEntryNotificationView,
} from '../client/types.js';
import type { ProposalKind, ProposalStatus } from '@autonomous-team/domain';
import { useStore, useStoreState } from './store-context.js';

export function useBoard(epicId?: string) {
  const store = useStore();
  const queryClient = useQueryClient();
  const legacyKey = epicId === undefined ? ['board'] : ['board', 'epic', epicId];
  const seeded = queryClient.getQueryData<BoardView>(legacyKey);
  /*
   * Live by polling: agents move cards on their own schedule, so the board
   * has to change under the person watching it — the local control plane
   * answers this read in single-digit milliseconds. In the background too,
   * because «switch tabs, come back, the card jumped» is the whole point.
   */
  const query = useInfiniteQuery({
    queryKey: epicId === undefined ? ['board', 'paged'] : ['board', 'epic', epicId, 'paged'],
    queryFn: ({ pageParam }) => store.data.board(epicId, pageParam),
    initialPageParam: null as string | null,
    ...(seeded === undefined ? {} : { initialData: { pages: [seeded], pageParams: [null] } }),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    refetchInterval: 2_500,
    refetchIntervalInBackground: true,
  });
  const pages = query.data?.pages;
  const data = pages === undefined ? undefined : {
    columns: pages[0]?.columns ?? [],
    hiddenColumns: pages[0]?.hiddenColumns ?? [],
    cards: pages.flatMap((page) => page.cards),
    nextCursor: pages.at(-1)?.nextCursor ?? null,
    ...(pages[0]?.stageTotals === undefined ? {} : { stageTotals: pages[0].stageTotals }),
    ...(pages[0]?.totalCards === undefined ? {} : { totalCards: pages[0].totalCards }),
  } satisfies BoardView;
  return { ...query, data };
}

export function useEpics() {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({ queryKey: ['epics', teamId], queryFn: () => store.data.epics() });
}

export function useEpic(epicId: string | null) {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: ['epic', teamId, epicId],
    queryFn: () => store.data.epic(epicId ?? ''),
    enabled: epicId !== null,
    refetchInterval: 1_000,
    refetchIntervalInBackground: true,
  });
}

export function useDeployments(
  page: { readonly limit: number; readonly offset: number },
  enabled = true,
): UseQueryResult<ReleasePageView> {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: releaseListQueryKey('deployments', teamId, page),
    queryFn: () => store.data.deployments(page),
    enabled,
  });
}

export function useDeployment(deploymentId: string | null): UseQueryResult<ReleaseOperationView> {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: releaseDetailQueryKey('deployments', teamId, deploymentId),
    queryFn: () => store.data.deployment(deploymentId ?? ''),
    enabled: deploymentId !== null,
  });
}

export function useReleases(
  page: { readonly limit: number; readonly offset: number },
  enabled = true,
): UseQueryResult<ReleasePageView> {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: releaseListQueryKey('releases', teamId, page),
    queryFn: () => store.data.releases(page),
    enabled,
  });
}

export function useRelease(releaseId: string | null): UseQueryResult<ReleaseOperationView> {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: releaseDetailQueryKey('releases', teamId, releaseId),
    queryFn: () => store.data.release(releaseId ?? ''),
    enabled: releaseId !== null,
  });
}

export function useReleasePlans() {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({ queryKey: ['release-plans', teamId], queryFn: () => store.data.releasePlans(), refetchInterval: 2_000 });
}

export function useReleaseConfiguration() {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({ queryKey: ['release-configuration', teamId], queryFn: () => store.data.releaseConfiguration() });
}

/**
 * Release writes happen through an agent, so our browser mutation hooks cannot
 * invalidate them. Reuse the shared SSE timeline: operation events invalidate
 * list/detail families and the board projection that carries card links.
 */
export function useReleaseEventInvalidation(): void {
  const client = useQueryClient();
  const relevantEvent = useStoreState((state) => {
    const entry = state.stream.timeline.find((candidate) => releaseEventInvalidates(candidate.eventType));
    return entry === undefined ? null : `${entry.cursor}:${entry.eventType}`;
  });

  useEffect(() => {
    if (relevantEvent === null) return;
    void Promise.all(
      [['deployments'], ['deployment'], ['releases'], ['release'], ['board']].map((queryKey) =>
        client.invalidateQueries({ queryKey }),
      ),
    );
  }, [client, relevantEvent]);
}

export function useRoles(): UseQueryResult<readonly RoleSummary[]> {
  const store = useStore();
  return useQuery({ queryKey: ['roles'], queryFn: () => store.data.roles() });
}

export function useHarnesses(): UseQueryResult<Awaited<ReturnType<ConsoleDataSource['harnesses']>>> {
  const store = useStore();
  return useQuery({ queryKey: ['harnesses'], queryFn: () => store.data.harnesses() });
}

export function useTeamStatus(): UseQueryResult<TeamStatus> {
  const store = useStore();
  return useQuery({ queryKey: ['team-status'], queryFn: () => store.data.teamStatus() });
}

export function useTasks(): UseQueryResult<readonly TaskListItem[]> {
  const store = useStore();
  return useQuery({ queryKey: ['tasks'], queryFn: () => store.data.tasks() });
}

export function useRuns(taskBindingId: string | null): UseQueryResult<readonly RunView[]> {
  const store = useStore();
  return useQuery({
    queryKey: ['runs', taskBindingId],
    queryFn: () => store.data.runs(taskBindingId ?? ''),
    enabled: taskBindingId !== null,
  });
}

export function useTaskChecklists(taskBindingId: string | null): UseQueryResult<readonly TaskChecklistView[]> {
  const store = useStore();
  return useQuery({
    queryKey: ['checklists', taskBindingId],
    queryFn: () => store.data.checklists(taskBindingId ?? ''),
    enabled: taskBindingId !== null,
    refetchInterval: 2_500,
  });
}

export function useAudit(limit = 50): UseQueryResult<readonly AuditEntry[]> {
  const store = useStore();
  return useQuery({ queryKey: ['audit', limit], queryFn: () => store.data.audit({ limit }) });
}

export function useQuorums(): UseQueryResult<Awaited<ReturnType<ConsoleDataSource['quorums']>>> {
  const store = useStore();
  return useQuery({ queryKey: ['quorums'], queryFn: () => store.data.quorums() });
}

export function useQuorumRuns(): UseQueryResult<Awaited<ReturnType<ConsoleDataSource['quorumRuns']>>> {
  const store = useStore();
  return useQuery({ queryKey: ['quorum-runs'], queryFn: () => store.data.quorumRuns() });
}

export function useUsage(range: { readonly from?: string; readonly to?: string }): UseQueryResult<
  Awaited<ReturnType<ConsoleDataSource['usage']>>
> {
  const store = useStore();
  return useQuery({ queryKey: ['usage', range.from ?? '', range.to ?? ''], queryFn: () => store.data.usage(range) });
}

export function useTaskUsage(taskBindingId: string | null): UseQueryResult<
  Awaited<ReturnType<ConsoleDataSource['taskUsage']>>
> {
  const store = useStore();
  return useQuery({
    queryKey: ['task-usage', taskBindingId ?? ''],
    queryFn: () => store.data.taskUsage(taskBindingId ?? ''),
    enabled: taskBindingId !== null,
  });
}

export function useHumanReviews(): UseQueryResult<readonly HumanReviewRequestView[]> {
  const store = useStore();
  return useQuery({ queryKey: ['human-reviews'], queryFn: () => store.data.humanReviews() });
}

export function useApprovals(): UseQueryResult<readonly ApprovalReceiptView[]> {
  const store = useStore();
  return useQuery({ queryKey: ['approvals'], queryFn: () => store.data.approvals() });
}

export function useGates(): UseQueryResult<readonly GateView[]> {
  const store = useStore();
  return useQuery({ queryKey: ['gates'], queryFn: () => store.data.gates() });
}

export function useIntegrations(): UseQueryResult<readonly IntegrationView[]> {
  const store = useStore();
  return useQuery({ queryKey: ['integrations'], queryFn: () => store.data.integrations() });
}

export function useCredentials(): UseQueryResult<readonly CredentialSummaryView[]> {
  const store = useStore();
  return useQuery({ queryKey: ['credentials'], queryFn: () => store.data.credentials() });
}

/**
 * The AI provider accounts this installation has (ADR-131).
 *
 * Installation-wide, so the key deliberately carries no team: two teams on one
 * machine share the same signed-in accounts whether or not the Console admits
 * it, and a per-team cache would have hidden exactly the sharing an operator
 * needs to see before disconnecting one.
 */
export function useProviderConnections(): UseQueryResult<readonly ProviderConnectionView[]> {
  const store = useStore();
  return useQuery({ queryKey: ['provider-connections'], queryFn: () => store.data.providerConnections() });
}

export function useStageEntryNotifications(): UseQueryResult<readonly StageEntryNotificationView[]> {
  const store = useStore();
  return useQuery({
    queryKey: ['stage-entry-notifications', store.currentTeamId],
    queryFn: () => store.data.stageEntryNotifications(),
    refetchInterval: 2_000,
  });
}

/** Login folders this machine already has, for the add form. */
export function useManagedLogins(): UseQueryResult<readonly ManagedLoginView[]> {
  const store = useStore();
  return useQuery({ queryKey: ['managed-logins'], queryFn: () => store.data.managedLogins() });
}

export function useWebhooks(): UseQueryResult<Awaited<ReturnType<ConsoleDataSource['webhooks']>>> {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({ queryKey: ['webhooks', teamId], queryFn: () => store.data.webhooks(teamId), refetchInterval: 5_000 });
}

export function useWebhookBatches(
  endpointId: string,
  endpointTeamId: string,
): UseQueryResult<Awaited<ReturnType<ConsoleDataSource['webhookBatches']>>> {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: ['webhook-batches', teamId, endpointId],
    queryFn: () => store.data.webhookBatches(endpointId, teamId),
    enabled: endpointTeamId === teamId,
    refetchInterval: 5_000,
  });
}

export function useSkills(): UseQueryResult<readonly SkillPackageView[]> {
  const store = useStore();
  return useQuery({ queryKey: ['skills'], queryFn: () => store.data.skills() });
}

export function useSkillContents(): UseQueryResult<Awaited<ReturnType<ReturnType<typeof useStore>['data']['skillContents']>>> {
  const store = useStore();
  return useQuery({ queryKey: ['skill-contents'], queryFn: () => store.data.skillContents() });
}

export function useTeamVersions(): UseQueryResult<readonly TeamVersionSummary[]> {
  const store = useStore();
  return useQuery({ queryKey: ['team-versions'], queryFn: () => store.data.teamVersions() });
}

/** The source attached to one saved version, cached with that version's identity. */
export function useTeamVersionDocument(teamId: string | null, version: number | null): UseQueryResult<string | null> {
  const store = useStore();
  return useQuery({
    queryKey: ['team-version-document', teamId ?? '', version],
    queryFn: () => store.data.teamVersionDocument(teamId ?? '', version ?? 0),
    enabled: teamId !== null && version !== null,
  });
}

export function useTemplates(): UseQueryResult<readonly TeamTemplate[]> {
  const store = useStore();
  return useQuery({ queryKey: ['templates'], queryFn: () => store.data.templates() });
}

/** Professions a role can have, from the compiler's own registry. */
export function usePackages(): UseQueryResult<readonly PackageOption[]> {
  const store = useStore();
  return useQuery({ queryKey: ['packages'], queryFn: () => store.data.packages() });
}

/** The workflow's columns and per-stage routing, from the active compiled plan. */
export function useTeamWorkflow(): UseQueryResult<WorkflowView> {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: ['team-workflow', teamId],
    queryFn: () => store.data.teamWorkflow(teamId),
    enabled: teamId.length > 0,
  });
}

export function useWorkflowLayout(topologySignature: string): UseQueryResult<WorkflowLayoutView | null> {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: ['workflow-layout', teamId, topologySignature],
    queryFn: () => store.data.workflowLayout(teamId, topologySignature),
    enabled: teamId.length > 0 && topologySignature.length > 0,
    refetchInterval: 5_000,
  });
}

export function useSaveWorkflowLayout() {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: Pick<WorkflowLayoutView, 'topologySignature' | 'positions' | 'waypoints'> & { readonly expectedRevision: number }) =>
      store.data.saveWorkflowLayout(teamId, input, mutationContext(store.config.actorId)),
    onSuccess: (layout) => {
      client.setQueryData(['workflow-layout', teamId, layout.topologySignature], layout);
    },
  });
}

export function useOperatorThread(enabled: boolean): UseQueryResult<MainOperatorThread> {
  const store = useStore();
  return useQuery({
    queryKey: ['operator-thread'],
    queryFn: () => store.data.mainOperatorThread(),
    enabled,
  });
}

/* ------------------------------------------------ operator chats (v12) --- */

/**
 * Quick, because `live` doubles as the chat's typing indicator: a reply in
 * progress should show as one within a breath, not within ten seconds.
 */
export function useOperatorStatus(): UseQueryResult<OperatorStatusView> {
  const store = useStore();
  return useQuery({
    queryKey: ['operator-status'],
    queryFn: () => store.data.operatorStatus(),
    refetchInterval: 3_000,
    refetchIntervalInBackground: true,
  });
}

export function useOperatorChats(): UseQueryResult<readonly OperatorChatSummary[]> {
  const store = useStore();
  return useQuery({
    queryKey: ['operator-chats'],
    queryFn: () => store.data.operatorChats(),
    refetchInterval: 5_000,
    refetchIntervalInBackground: true,
  });
}

/**
 * One conversation, polled while open: the agent answers on its own schedule
 * through the daemon's loop, and a reply must appear without anybody typing.
 * Polled in background tabs too — an operator's reply arriving while the
 * person reads something else is the normal case, not the exception.
 */
/** Every team on this control plane, for the switcher. */
export function useTeams(): UseQueryResult<readonly TeamListItem[]> {
  const store = useStore();
  return useQuery({
    queryKey: ['teams'],
    queryFn: () => store.data.listTeams(),
    refetchInterval: 30_000,
  });
}

/** The agent's working thread behind a role or fork chat, polled while open. */
export function useHomeThread(chatId: string | null, enabled: boolean): UseQueryResult<readonly HomeThreadMessage[]> {
  const store = useStore();
  return useQuery({
    queryKey: ['home-thread', chatId],
    queryFn: () => store.data.homeThread(chatId ?? ''),
    enabled: chatId !== null && enabled,
    refetchInterval: 750,
    refetchIntervalInBackground: true,
  });
}

export function useOperatorChat(chatId: string | null): UseQueryResult<OperatorChatThread> {
  const store = useStore();
  return useQuery({
    queryKey: ['operator-chat', chatId],
    queryFn: () => store.data.operatorChat(chatId ?? ''),
    enabled: chatId !== null,
    // Progress is produced token-by-token in memory by the harness. A slow
    // multi-second poll made that indistinguishable from a final-only reply.
    refetchInterval: 250,
    refetchIntervalInBackground: true,
  });
}

export function useTaskChat(taskBindingId: string | null, enabled = true): UseQueryResult<OperatorChatThread> {
  const store = useStore();
  return useQuery({
    queryKey: ['task-chat', taskBindingId, store.currentTeamId],
    queryFn: () => store.data.taskChat(taskBindingId ?? ''),
    enabled: taskBindingId !== null && enabled,
    refetchInterval: (query) => isTaskNotFoundError(query.state.error) ? false : 250,
    refetchIntervalInBackground: true,
  });
}


/**
 * An attachment's bytes as an object URL the markup can reference.
 *
 * Fetched with the session header — an `<img src>` cannot carry one — and
 * cached forever, because the store is content-addressed: the bytes behind a
 * digest can never change. The URL is intentionally never revoked; a chat's
 * worth of attachments is small and revocation would break re-renders.
 */
export function useAttachmentUrlState(digest: string | null): { readonly url: string | null; readonly loading: boolean; readonly failed: boolean } {
  const store = useStore();
  const query = useQuery({
    queryKey: ['attachment-url', digest],
    queryFn: async () => {
      if (digest === null) throw new Error('an attachment digest is required');
      return URL.createObjectURL(await store.data.attachmentBlob(digest));
    },
    enabled: digest !== null,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
  });
  return { url: query.data ?? null, loading: digest !== null && query.isPending, failed: digest !== null && query.isError };
}

export function useAttachmentUrl(digest: string | null): string | null {
  return useAttachmentUrlState(digest).url;
}

export function useProposals(filter: { readonly kind?: ProposalKind; readonly status?: ProposalStatus; readonly sourceTaskBindingId?: string }) {
  const store = useStore();
  const teamId = useStoreState((state) => state.currentTeamId);
  return useQuery({
    queryKey: ['proposals', teamId, filter.kind ?? 'all', filter.status ?? 'all', filter.sourceTaskBindingId ?? 'all-tasks'],
    queryFn: () => store.data.proposals(filter),
    ...(filter.sourceTaskBindingId === undefined ? {} : { refetchInterval: 1_000, refetchIntervalInBackground: true }),
  });
}

/* --------------------------------------------------------------- writes --- */

/**
 * Wraps a mutation so its idempotency key and actor are supplied once.
 *
 * §20.1 requires both on every mutation. Threading them through each call site
 * is how one eventually goes missing, and a mutation without a key is a
 * mutation that can be applied twice by a retry.
 */
export function useConsoleMutation<TArgs, TResult>(
  run: (data: ReturnType<typeof useStore>['data'], context: ReturnType<typeof mutationContext>, args: TArgs) => Promise<TResult>,
  invalidate: readonly (readonly unknown[])[],
): ReturnType<typeof useMutation<TResult, Error, TArgs>> {
  const store = useStore();
  const client = useQueryClient();

  return useMutation<TResult, Error, TArgs>({
    mutationFn: (args: TArgs) => run(store.data, mutationContext(store.config.actorId), args),
    onSuccess: async () => {
      await Promise.all(invalidate.map((key) => client.invalidateQueries({ queryKey: key })));
    },
  });
}
